import React, { useEffect, useState, useRef } from 'react';
import SupplierProductSearch from '../components/SupplierProductSearch';
import ProfileCompleteness from '../components/ProfileCompleteness';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronUp, ChevronDown, Package, ShoppingBag, Plus, Trash2, Edit, Clock, Users, AlertCircle, RefreshCw, Search, Tag, X, Upload, Percent, Zap, Truck, Settings, Store, QrCode, Image as ImageIcon, MessageSquare } from 'lucide-react';
import { SupplierAPI, MessagingAPI, CatalogAPI, formatPrice, formatRelative, resolveImage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import OnboardingChecklist from '../components/supplier/OnboardingChecklist';
import { WILAYAS } from '../data/wilayas';

const SupplierDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') || 'orders');
  const [products, setProducts] = useState([]);
  const [storeInfo, setStoreInfo] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stale, setStale] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerPreselected, setOfferPreselected] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const refreshAll = () => {
    SupplierAPI.products().then(setProducts);
    SupplierAPI.orders().then(setOrders);
    SupplierAPI.staleOffers().then(setStale);
    SupplierAPI.me().then(setStoreInfo).catch(() => {});
  };

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    if (user.role !== 'supplier') { navigate('/'); return; }
    refreshAll();
    CatalogAPI.categories().then(setCategories);
    CatalogAPI.brands().then(setBrands);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  if (!user || user.role !== 'supplier') return null;

  if (!user.approved) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <Clock className="w-12 h-12 text-[#E0A52E] mx-auto mb-3" />
        <h1 className="font-serif text-2xl font-semibold text-[#1F4D3F]">Compte en attente de validation</h1>
        <p className="text-[#7A6F5C] mt-2">Notre équipe examine votre demande. Vous recevrez une notification dès validation.</p>
      </div>
    );
  }

  const onDelete = async (id) => {
    if (!confirm('Retirer vos offres sur ce produit ?')) return;
    await SupplierAPI.deleteProduct(id);
    toast({ title: 'Offres retirées' });
    refreshAll();
  };

  const contactClient = async (buyerId, orderId) => {
    try {
      const res = await MessagingAPI.start(buyerId, `order:${orderId}`, '');
      window.location.href = `/messages?c=${res.conversation_id}`;
    } catch (e) { /* silencieux */ }
  };
  const onUpdateOrder = async (oid, status) => {
    await SupplierAPI.updateOrderStatus(oid, status);
    toast({ title: 'Statut mis à jour' });
    SupplierAPI.orders().then(setOrders);
  };

  // Quick availability inline update
  const quickUpdate = async (productId, variantLabel, patch) => {
    try {
      await SupplierAPI.updateAvailability({ product_id: productId, variant_label: variantLabel, ...patch });
      toast({ title: 'Disponibilité mise à jour' });
      refreshAll();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec' });
    }
  };

  const supplierId = user.supplier_id;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">Espace fournisseur</span>
      </nav>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F]">Tableau de bord</h1>
          <p className="text-[#7A6F5C] mt-1">Bonjour {user.name} · {user.company || ''}</p>
        </div>
        {tab === 'products' && (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setOfferOpen(true)} variant="outline" className="border-[#1F4D3F] text-[#1F4D3F] hover:bg-[#FAF7F2]">
              <Tag className="w-4 h-4 mr-1" /> Offre sur produit existant
            </Button>
            <Button onClick={() => setBulkOpen(true)} variant="outline" className="border-[#1F4D3F] text-[#1F4D3F] hover:bg-[#FAF7F2]">
              <Zap className="w-4 h-4 mr-1" /> Outils en masse
            </Button>
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
              <Plus className="w-4 h-4 mr-1" /> Nouveau produit
            </Button>
          </div>
        )}
      </div>

      {/* Onboarding checklist (auto-hides when complete or dismissed) */}
      <OnboardingChecklist
        supplierId={user.supplier_id}
        onStepClick={(stepId) => {
          if (stepId === 'profile' || stepId === 'shipping') setTab('settings');
          else if (stepId === 'product') { setEditing(null); setOpen(true); }
          else if (stepId === 'offer') setTab('products');
          // Scroll user down to the now-active section so the click feels responsive
          requestAnimationFrame(() => {
            setTimeout(() => {
              const el = document.querySelector('[data-testid="supplier-tabs-bar"]');
              if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } else {
                window.scrollTo({ top: 700, behavior: 'smooth' });
              }
            }, 150);
          });
        }}
      />

      {/* Stale alert banner */}
      {stale.length > 0 && tab !== 'stale' && (
        <button onClick={() => setTab('stale')} className="w-full mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between text-left hover:bg-amber-100/60 transition">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-700" />
            <div>
              <div className="font-medium text-amber-900">{stale.length} offre{stale.length > 1 ? 's' : ''} à mettre à jour</div>
              <div className="text-xs text-amber-700">Vos disponibilités datent de plus de 7 jours — cliquez pour les actualiser.</div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-700" />
        </button>
      )}

      {storeInfo && (
        <ProfileCompleteness
          store={storeInfo}
          productsCount={products.length}
          onGoTo={(t) => setTab(t)}
        />
      )}
      <div className="flex gap-2 mb-6 border-b border-[#E8E0D0] overflow-x-auto" data-testid="supplier-tabs-bar">
        {[
          { id: 'orders', label: 'Commandes', icon: ShoppingBag, count: orders.length },
          { id: 'boutique', label: 'Ma boutique', icon: Store, count: null },
          { id: 'products', label: 'Mes produits', icon: Package, count: products.length },
          { id: 'stale', label: 'À actualiser', icon: RefreshCw, count: stale.length },
          { id: 'settings', label: 'Paramètres', icon: Settings, count: null },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`supplier-tab-${t.id}`}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${
                tab === t.id ? 'border-[#1F4D3F] text-[#1F4D3F]' : 'border-transparent text-[#7A6F5C]'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {t.count !== null && <span className={`text-xs px-1.5 rounded-full ${tab === t.id ? 'bg-[#1F4D3F] text-white' : 'bg-[#F5F1EA] text-[#7A6F5C]'}`}>{t.count}</span>}
            </button>
          );
        })}
      </div>

      {tab === 'products' && (
        <div className="space-y-3">
          {/* Quick-add toolbar inside the products tab (Issue #2) */}
          <div className="flex items-center justify-between gap-2 bg-white border border-[#E8E0D0] rounded-xl p-3" data-testid="products-tab-toolbar">
            <div className="text-sm text-[#5A5247]">
              <span className="font-medium text-[#2D2A26]">{products.length}</span> produit{products.length !== 1 ? 's' : ''} en catalogue
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => setOfferOpen(true)}
                variant="outline"
                size="sm"
                data-testid="products-tab-add-offer"
                className="text-[#1F4D3F] border-[#1F4D3F] hover:bg-[#FAF7F2]">
                <Tag className="w-4 h-4 mr-1.5" /> Offre sur produit existant
              </Button>
              <Button
                onClick={() => { setEditing(null); setOpen(true); }}
                size="sm"
                data-testid="products-tab-new-product"
                className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Nouveau produit
              </Button>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="bg-white border border-[#E8E0D0] rounded-2xl p-10 text-center text-[#7A6F5C]">Aucun produit. Cliquez sur "Nouveau produit".</div>
          ) : (
            products.map((p) => (
              <ProductRow
                key={p.id}
                product={p}
                supplierId={supplierId}
                onEdit={() => { setEditing(p); setOpen(true); }}
                onDelete={() => onDelete(p.id)}
                onQuickUpdate={quickUpdate}
              />
            ))
          )}
        </div>
      )}

      {tab === 'stale' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
          {stale.length === 0 ? (
            <div className="p-10 text-center text-[#7A6F5C]">Toutes vos offres sont à jour ✨</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Produit</th>
                  <th className="px-4 py-3">Conditionnement</th>
                  <th className="px-4 py-3">Dernière maj</th>
                  <th className="px-4 py-3">Qté min</th>
                  <th className="px-4 py-3">Prix</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {stale.map((s) => (
                  <StaleRow key={`${s.product_id}-${s.variant_label}`} stale={s} onQuickUpdate={quickUpdate} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'boutique' && <StoreManager />}
      {tab === 'catalogue' && <SupplierProductSearch onOfferAdded={refreshAll} />}
{tab === 'orders' && (
        <div className="space-y-3">
          {orders.length === 0 ? (
            <div className="bg-white border border-[#E8E0D0] rounded-2xl p-10 text-center text-[#7A6F5C]">Aucune commande pour l'instant.</div>
          ) : orders.map((o) => {
            const status = o.status || 'pending';
            const statusLabel = {
              pending:   { label: 'En attente d\'approbation', color: 'bg-amber-100 text-amber-900 border-amber-300' },
              preparing: { label: 'Approuvée · en préparation', color: 'bg-blue-100 text-blue-900 border-blue-300' },
              shipped:   { label: 'Expédiée', color: 'bg-indigo-100 text-indigo-900 border-indigo-300' },
              delivered: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
              cancelled: { label: 'Annulée', color: 'bg-rose-100 text-rose-900 border-rose-300' },
            }[status] || { label: status, color: 'bg-slate-100 text-slate-900 border-slate-300' };

            return (
            <div key={o.id} data-testid={`supplier-order-${o.id}`} className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#F5F1EA]">
                <div>
                  <div className="font-medium text-[#2D2A26]">{o.id}</div>
                  <div className="text-xs text-[#7A6F5C]">{new Date(o.created_at).toLocaleString('fr-DZ')}</div>
                </div>
                <span data-testid={`order-status-${o.id}`}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusLabel.color}`}>
                  {statusLabel.label}
                </span>
              </div>
              <div className="py-3 space-y-2">
                {o.items.map((it) => (
                  <div key={`${it.product_id}-${it.variant_label}`} className="flex items-center gap-3 text-sm">
                    <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-[#2D2A26]">{it.name}</div>
                      <div className="text-xs text-[#7A6F5C]">{it.variant_label} · x{it.qty}</div>
                    </div>
                    <div className="font-medium text-[#1F4D3F]">{formatPrice(it.subtotal)}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-[#F5F1EA] mb-3">
                <div className="text-xs text-[#7A6F5C]">Total de la commande</div>
                <div className="font-serif text-lg font-semibold text-[#1F4D3F]">{formatPrice(o.total_for_supplier)}</div>
              </div>
              {/* Coordonnées client pour la livraison */}
              {o.shipping_address && (
                <div className="bg-[#FAF7F2] border border-[#E8E0D0] rounded-xl p-3 mb-3 text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-[#1F4D3F] font-semibold mb-2">Livraison</div>
                  <div className="space-y-1 text-[#2D2A26]">
                    <div className="flex items-center gap-2"><span className="text-[#7A6F5C]">Client :</span><span className="font-medium">{o.shipping_address.full_name}</span></div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#7A6F5C]">Tél :</span>
                      <a href={`tel:${o.shipping_address.phone}`} className="font-medium text-[#1F4D3F] underline">{o.shipping_address.phone}</a>
                      <a href={`tel:${o.shipping_address.phone}`} className="ml-1 text-xs bg-[#1F4D3F] text-white px-2 py-0.5 rounded">Appeler</a>
                    </div>
                    <div className="flex items-start gap-2"><span className="text-[#7A6F5C]">Adresse :</span><span className="font-medium">{o.shipping_address.address}, {o.shipping_address.city}, {o.shipping_address.wilaya}</span></div>
                    {o.shipping_address.notes && <div className="flex items-start gap-2"><span className="text-[#7A6F5C]">Note :</span><span className="italic">{o.shipping_address.notes}</span></div>}
                  </div>
                  {o.buyer_user_id && (
                    <button onClick={() => contactClient(o.buyer_user_id, o.id)}
                      className="mt-3 w-full bg-white border border-[#1F4D3F] text-[#1F4D3F] hover:bg-[#1F4D3F] hover:text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-1.5 transition">
                      <MessageSquare className="w-4 h-4" /> Contacter le client
                    </button>
                  )}
                </div>
              )}

              {/* Action buttons — drive the order forward */}
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#F5F1EA]">
                {status === 'pending' && (
                  <>
                    <Button onClick={() => onUpdateOrder(o.id, 'preparing')} size="sm"
                      data-testid={`order-approve-${o.id}`}
                      className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
                      ✓ Approuver la commande
                    </Button>
                    <Button onClick={() => onUpdateOrder(o.id, 'cancelled')} size="sm" variant="outline"
                      data-testid={`order-cancel-${o.id}`}
                      className="text-rose-700 border-rose-300 hover:bg-rose-50">
                      Refuser
                    </Button>
                  </>
                )}
                {status === 'preparing' && (
                  <Button onClick={() => onUpdateOrder(o.id, 'shipped')} size="sm"
                    data-testid={`order-ship-${o.id}`}
                    className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
                    🚚 Marquer comme expédiée
                  </Button>
                )}
                {status === 'shipped' && (
                  <Button onClick={() => onUpdateOrder(o.id, 'delivered')} size="sm"
                    data-testid={`order-deliver-${o.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    ✓ Marquer comme livrée
                  </Button>
                )}
                {status === 'delivered' && (
                  <div className="text-xs text-emerald-700 font-medium">Commande livrée · le client a été notifié par email.</div>
                )}
                {status === 'cancelled' && (
                  <div className="text-xs text-rose-700 font-medium">Commande annulée.</div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {tab === 'settings' && (
        <SupplierSettingsPanel />
      )}

      <ProductFormDialog
        open={open}
        onOpenChange={setOpen}
        categories={categories}
        brands={brands}
        editing={editing}
        onTaxonomyRefresh={() => {
          CatalogAPI.categories().then(setCategories);
          CatalogAPI.brands().then(setBrands);
        }}
        onSelectExisting={(product) => {
          // Issue #3: user picked a product that already exists — switch to offer flow
          setOpen(false);
          setEditing(null);
          // Pass the pre-selected product to AddOfferDialog via state
          setOfferPreselected(product);
          setOfferOpen(true);
        }}
        onDone={() => { setOpen(false); refreshAll(); }}
      />
      <AddOfferDialog
        open={offerOpen}
        onOpenChange={(v) => { setOfferOpen(v); if (!v) setOfferPreselected(null); }}
        preselected={offerPreselected}
        onDone={() => { setOfferOpen(false); setOfferPreselected(null); refreshAll(); }}
      />
      <BulkToolsDialog open={bulkOpen} onOpenChange={setBulkOpen} categories={categories} onDone={() => { setBulkOpen(false); refreshAll(); }} />
    </div>
  );
};

// --- Sub-components ---

const ProductRow = ({ product, supplierId, onEdit, onDelete, onQuickUpdate }) => {
  const myVariants = (product.variants || []).filter((v) => v.supplier_id === supplierId);
  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl p-4">
      <div className="flex items-start gap-3">
        <img src={product.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-[#2D2A26] truncate">{product.name}</div>
              <div className="text-xs text-[#7A6F5C]">{product.brand} · <span className="capitalize">{product.category}</span> · {myVariants.length} variante{myVariants.length > 1 ? 's' : ''}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={onEdit} className="p-2 text-[#1F4D3F] hover:bg-[#FAF7F2] rounded" aria-label="Modifier"><Edit className="w-4 h-4" /></button>
              <button onClick={onDelete} className="p-2 text-[#C75B27] hover:bg-[#FAF7F2] rounded" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="mt-3 space-y-2">
            {myVariants.map((v) => (
              <VariantRow key={`${product.id}-${v.label}`} productId={product.id} variant={v} onQuickUpdate={onQuickUpdate} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const VariantRow = ({ productId, variant, onQuickUpdate }) => {
  const [price, setPrice] = useState(variant.price);
  const [minQty, setMinQty] = useState(variant.min_qty || 1);
  const [inStock, setInStock] = useState(variant.in_stock !== false);
  const [saving, setSaving] = useState(false);
  const dirty = price !== variant.price || Number(minQty) !== (variant.min_qty || 1) || inStock !== (variant.in_stock !== false);

  const save = async () => {
    setSaving(true);
    await onQuickUpdate(productId, variant.label, { price: Number(price), min_qty: Math.max(1, parseInt(minQty) || 1), stock: inStock ? 999 : 0, in_stock: inStock });
    setSaving(false);
  };

  const renew = async () => {
    setSaving(true);
    // Refresh last_updated without changing anything
    await onQuickUpdate(productId, variant.label, { price: Number(price), min_qty: Math.max(1, parseInt(minQty) || 1), stock: inStock ? 999 : 0, in_stock: inStock });
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1.2fr_110px_90px_130px_120px_auto_auto] gap-2 items-center bg-[#FAF7F2] rounded-lg p-2">
      <div className="text-sm text-[#2D2A26] truncate">{variant.label}</div>
      <div>
        <label className="text-[10px] text-[#7A6F5C] uppercase">Prix DA</label>
        <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="h-8 text-sm" />
      </div>
      <div>
        <label className="text-[10px] text-[#7A6F5C] uppercase">Qté min</label>
        <Input type="number" min="1" value={minQty} onChange={(e) => setMinQty(e.target.value)} className="h-8 text-sm" />
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} className="w-4 h-4 accent-[#1F4D3F]" />
        Disponible
      </label>
      <div className="text-[11px] text-[#7A6F5C] truncate">{formatRelative(variant.last_updated)}</div>
      <Button size="sm" disabled={saving || dirty} onClick={renew} variant="outline" className="border-[#1F4D3F] text-[#1F4D3F] hover:bg-white h-8" title="Confirmer la disponibilité sans rien changer">
        <RefreshCw className="w-3.5 h-3.5" />
      </Button>
      <Button size="sm" disabled={!dirty || saving} onClick={save} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white disabled:bg-gray-300 h-8">
        {saving ? '...' : 'Mettre à jour'}
      </Button>
    </div>
  );
};

const StaleRow = ({ stale, onQuickUpdate }) => {
  const [price, setPrice] = useState(stale.price);
  const [minQty, setMinQty] = useState(stale.min_qty || 1);
  const [inStock, setInStock] = useState(stale.in_stock !== false);

  const save = () => onQuickUpdate(stale.product_id, stale.variant_label, { price: Number(price), min_qty: Math.max(1, parseInt(minQty) || 1), stock: inStock ? 999 : 0, in_stock: inStock });

  return (
    <tr className="border-t border-[#F5F1EA]">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <img src={stale.image} alt="" className="w-10 h-10 rounded object-cover" />
          <div className="text-[#2D2A26] font-medium text-sm">{stale.product_name}</div>
        </div>
      </td>
      <td className="px-4 py-3 text-[#5A5247] text-sm">{stale.variant_label}</td>
      <td className="px-4 py-3 text-xs text-rose-700 font-medium">{formatRelative(stale.last_updated)}</td>
      <td className="px-4 py-3"><Input type="number" min="1" value={minQty} onChange={(e) => setMinQty(e.target.value)} className="h-8 w-20 text-sm" /></td>
      <td className="px-4 py-3"><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="h-8 w-28 text-sm" /></td>
      <td className="px-4 py-3 text-right">
        <label className="inline-flex items-center gap-1.5 text-xs mr-2">
          <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} className="w-4 h-4 accent-[#1F4D3F]" />
          Dispo
        </label>
        <Button size="sm" onClick={save} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white h-8">Actualiser</Button>
      </td>
    </tr>
  );
};

const ProductFormDialog = ({ open, onOpenChange, categories, brands, editing, onDone, onTaxonomyRefresh, onSelectExisting }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', brand: '', category: '', image: '', description: '', discount: '', variants: [{ label: '1 unité', price: 0, min_qty: 1, in_stock: true }] });
  const [brandInput, setBrandInput] = useState('');
  const [catInput, setCatInput] = useState('');
  const [showBrandNew, setShowBrandNew] = useState(false);
  const [showCatNew, setShowCatNew] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsHidden, setSuggestionsHidden] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name, brand: editing.brand, category: editing.category, image: editing.image,
        description: editing.description || '',
        discount: editing.discount || '',
        variants: (editing.variants || []).filter(v => v.supplier_id === user?.supplier_id).map(v => ({ label: v.label, price: v.price, min_qty: v.min_qty || 1, in_stock: v.in_stock !== false })),
      });
    } else {
      setForm({ name: '', brand: '', category: '', image: 'https://images.unsplash.com/photo-1495573020741-8a2f372bbec3?w=500&q=80', description: '', discount: '', variants: [{ label: '1 unité', price: 0, min_qty: 1, in_stock: true }] });
    }
    setBrandInput(''); setCatInput(''); setShowBrandNew(false); setShowCatNew(false);
    setSuggestions([]); setSuggestionsHidden(false);
  }, [editing, user, open]);

  // Issue #3: debounced search for existing products as the user types the name
  useEffect(() => {
    if (editing || suggestionsHidden) return;
    const q = (form.name || '').trim();
    if (q.length < 3) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      CatalogAPI.products({ q, limit: 5, include_empty: true })
        .then((data) => {
          const items = Array.isArray(data) ? data : (data?.items || []);
          const supId = user?.supplier_id;
          // Exclude products where the supplier already has a variant (avoids self-suggestion)
          setSuggestions(items.filter(p => !(p.variants || []).some(v => v.supplier_id === supId)).slice(0, 5));
        })
        .catch(() => setSuggestions([]));
    }, 400);
    return () => clearTimeout(t);
  }, [form.name, editing, user, suggestionsHidden]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand) { toast({ title: 'Marque requise' }); return; }
    if (!form.category) { toast({ title: 'Catégorie requise' }); return; }
    const payload = {
      name: form.name, brand: form.brand, category: form.category, image: form.image,
      description: form.description || null,
      discount: form.discount ? Number(form.discount) : null,
      variants: form.variants.map(v => ({ label: v.label, price: Number(v.price), min_qty: Math.max(1, parseInt(v.min_qty) || 1), stock: v.in_stock ? 999 : 0, in_stock: !!v.in_stock, supplier_id: user.supplier_id })),
    };
    try {
      if (editing) await SupplierAPI.updateProduct(editing.id, payload);
      else await SupplierAPI.createProduct(payload);
      toast({ title: editing ? 'Produit modifié' : 'Produit créé' });
      onDone();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Opération impossible' });
    }
  };

  const updateVariant = (idx, field, val) => {
    const next = [...form.variants];
    next[idx] = { ...next[idx], [field]: val };
    setForm({ ...form, variants: next });
  };

  const addNewBrand = async () => {
    const name = brandInput.trim();
    if (!name) return;
    try {
      await SupplierAPI.createBrand(name);
      setForm({ ...form, brand: name });
      setShowBrandNew(false); setBrandInput('');
      onTaxonomyRefresh?.();
      toast({ title: 'Marque ajoutée' });
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec' });
    }
  };

  const addNewCategory = async () => {
    const name = catInput.trim();
    if (!name) return;
    try {
      const res = await SupplierAPI.createCategory({ name });
      setForm({ ...form, category: res.id });
      setShowCatNew(false); setCatInput('');
      onTaxonomyRefresh?.();
      toast({ title: 'Catégorie ajoutée' });
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1F4D3F]">{editing ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-sm">Nom du produit</label>
            <Input required value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setSuggestionsHidden(false); }} placeholder="Ex: Composite universel A3" data-testid="product-form-name" />

            {/* Issue #3: suggest existing products from the platform */}
            {!editing && suggestions.length > 0 && !suggestionsHidden && (
              <div className="mt-2 border border-amber-200 bg-amber-50/60 rounded-lg p-3" data-testid="existing-product-suggestions">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-amber-900 inline-flex items-center gap-1.5">
                    <Search className="w-3.5 h-3.5" /> Ce produit existe déjà sur la plateforme :
                  </div>
                  <button type="button" onClick={() => setSuggestionsHidden(true)}
                    data-testid="dismiss-suggestions"
                    className="text-xs text-amber-700 hover:underline">
                    Continuer la création
                  </button>
                </div>
                <div className="space-y-1.5">
                  {suggestions.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`suggestion-${p.id}`}
                      onClick={() => onSelectExisting?.(p)}
                      className="w-full flex items-center gap-3 text-left p-2 rounded-md bg-white hover:bg-amber-100/50 border border-transparent hover:border-amber-300 transition">
                      <img src={p.image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#2D2A26] truncate">{p.name}</div>
                        <div className="text-xs text-[#7A6F5C]">{p.brand} · {(p.variants || []).length} variante(s)</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-amber-800 mt-2">
                  💡 Cliquez pour ajouter votre offre dessus au lieu de créer un doublon.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm">Marque</label>
                <button type="button" onClick={() => setShowBrandNew(!showBrandNew)} className="text-xs text-[#1F4D3F] hover:underline">{showBrandNew ? 'Annuler' : '+ Nouvelle'}</button>
              </div>
              {!showBrandNew ? (
                <select required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full border border-[#E8E0D0] rounded-md px-3 py-2 bg-white text-sm">
                  <option value="">Sélectionner...</option>
                  {brands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              ) : (
                <div className="flex gap-1">
                  <Input value={brandInput} onChange={(e) => setBrandInput(e.target.value)} placeholder="Nouvelle marque" />
                  <Button type="button" onClick={addNewBrand} size="sm" className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">OK</Button>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm">Catégorie</label>
                <button type="button" onClick={() => setShowCatNew(!showCatNew)} className="text-xs text-[#1F4D3F] hover:underline">{showCatNew ? 'Annuler' : '+ Nouvelle'}</button>
              </div>
              {!showCatNew ? (
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[#E8E0D0] rounded-md px-3 py-2 bg-white text-sm">
                  <option value="">Sélectionner...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              ) : (
                <div className="flex gap-1">
                  <Input value={catInput} onChange={(e) => setCatInput(e.target.value)} placeholder="Nouvelle catégorie" />
                  <Button type="button" onClick={addNewCategory} size="sm" className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">OK</Button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#2D2A26]">Photo du produit</label>
            <div className="mt-1.5 flex items-start gap-3">
              <div className="w-24 h-24 rounded-lg border border-[#E8E0D0] bg-[#FAF7F2] overflow-hidden flex-shrink-0">
                {form.image ? (
                  <img src={form.image.startsWith('http') ? form.image : `${process.env.REACT_APP_BACKEND_URL}${form.image}`}
                    alt="" className="w-full h-full object-cover" data-testid="product-form-image-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-[#7A6F5C] p-2 text-center">Aucune photo</div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 bg-[#1F4D3F] hover:bg-[#173B30] text-white text-sm font-medium px-3 py-2 rounded-md cursor-pointer" data-testid="product-form-image-upload">
                  <Upload className="w-4 h-4" />
                  {uploadingImage ? 'Envoi en cours…' : 'Choisir une photo (téléphone ou ordinateur)'}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={uploadingImage}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setUploadingImage(true);
                      try {
                        const res = await SupplierAPI.uploadImage(f);
                        setForm((s) => ({ ...s, image: `${process.env.REACT_APP_BACKEND_URL}${res.url}` }));
                        toast({ title: 'Photo téléversée' });
                      } catch (err) {
                        toast({ title: 'Erreur', description: err.response?.data?.detail || 'Upload échoué' });
                      } finally {
                        setUploadingImage(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
                <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="… ou collez une URL d'image"
                  data-testid="product-form-image-url"
                  className="text-xs" />
                <p className="text-[11px] text-[#7A6F5C]">JPG/PNG/WebP — max 5 Mo. Une seule photo suffit, elle sera réutilisée par tous les fournisseurs qui ajoutent ce produit.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm">Remise % (optionnel)</label>
            <Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
          </div>

          <div><label className="text-sm">Description (optionnel)</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full border border-[#E8E0D0] rounded-md p-2 text-sm" /></div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Conditionnements & prix</label>
              <button type="button" onClick={() => setForm({ ...form, variants: [...form.variants, { label: '', price: 0, min_qty: 1, in_stock: true }] })} className="text-xs text-[#1F4D3F] hover:underline">+ Ajouter</button>
            </div>
            {form.variants.map((v, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_110px_90px_90px_30px] gap-2 mb-2 items-center">
                <Input placeholder="Libellé" value={v.label} onChange={(e) => updateVariant(idx, 'label', e.target.value)} />
                <Input type="number" placeholder="Prix DA" value={v.price} onChange={(e) => updateVariant(idx, 'price', e.target.value)} />
                <Input type="number" placeholder="Qté min" value={v.min_qty || 1} onChange={(e) => updateVariant(idx, 'min_qty', e.target.value)} />
                <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={v.in_stock} onChange={(e) => updateVariant(idx, 'in_stock', e.target.checked)} className="accent-[#1F4D3F]" /> Dispo</label>
                {form.variants.length > 1 && (
                  <button type="button" onClick={() => setForm({ ...form, variants: form.variants.filter((_, i) => i !== idx) })} className="text-[#C75B27]"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">{editing ? 'Enregistrer' : 'Créer le produit'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AddOfferDialog = ({ open, onOpenChange, onDone, preselected }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [variants, setVariants] = useState([{ label: 'Unité', price: 0, min_qty: 1, in_stock: true }]);

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); setSelected(null); setVariants([{ label: 'Unité', price: 0, min_qty: 1, in_stock: true }]); }
    else if (preselected) {
      // Issue #3: jump directly to the variant form when a product was pre-selected
      setSelected(preselected);
    }
  }, [open, preselected]);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => { CatalogAPI.products({ q, limit: 8, include_empty: true }).then(setResults); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async () => {
    if (!selected) { toast({ title: 'Sélectionnez un produit' }); return; }
    const clean = variants
      .filter(v => v.label && v.label.trim() && Number(v.price) > 0)
      .map(v => ({ label: v.label.trim(), price: Number(v.price), min_qty: Math.max(1, parseInt(v.min_qty) || 1), stock: v.in_stock ? 999 : 0, in_stock: !!v.in_stock, supplier_id: user.supplier_id }));
    if (clean.length === 0) {
      toast({ title: 'Complétez votre offre', description: 'Indiquez au moins un libellé et un prix.' });
      return;
    }
    try {
      await SupplierAPI.addOffer({ product_id: selected.id, variants: clean });
      toast({ title: 'Offre ajoutée au produit ✅' });
      onDone();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec de l\'ajout' });
    }
  };

  const updateVariant = (idx, field, val) => {
    const next = [...variants];
    next[idx] = { ...next[idx], [field]: val };
    setVariants(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1F4D3F]">Ajouter une offre sur un produit existant</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!selected ? (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-[#7A6F5C]" />
                <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit (2 caractères min)" className="pl-9" />
              </div>
              <div className="max-h-64 overflow-y-auto border border-[#E8E0D0] rounded-lg">
                {results.length === 0 ? (
                  <div className="p-4 text-sm text-[#7A6F5C] text-center">{q.length < 2 ? 'Tapez pour rechercher...' : 'Aucun résultat'}</div>
                ) : results.map((p) => (
                  <button key={p.id} type="button" onClick={() => setSelected(p)} className="w-full text-left p-3 flex items-center gap-3 hover:bg-[#FAF7F2] border-b border-[#F5F1EA] last:border-0">
                    <img src={p.image} alt="" className="w-10 h-10 rounded object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-[#2D2A26] truncate">{p.name}</div>
                      <div className="text-xs text-[#7A6F5C]">{p.brand} · {p.variants?.length || 0} offre(s) existante(s)</div>
                    </div>
                    <Plus className="w-4 h-4 text-[#1F4D3F]" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="bg-[#FAF7F2] rounded-lg p-3 flex items-center gap-3">
                <img src={selected.image} alt="" className="w-12 h-12 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#2D2A26] truncate">{selected.name}</div>
                  <div className="text-xs text-[#7A6F5C]">{selected.brand}</div>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-[#7A6F5C] hover:text-[#C75B27]"><X className="w-4 h-4" /></button>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Vos conditionnements pour ce produit</label>
                  <button type="button" onClick={() => setVariants([...variants, { label: '', price: 0, min_qty: 1, in_stock: true }])} className="text-xs text-[#1F4D3F] hover:underline">+ Ajouter</button>
                </div>
                {variants.map((v, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_100px_90px_90px_30px] gap-2 mb-2 items-center">
                    <Input placeholder="Libellé" value={v.label} onChange={(e) => updateVariant(idx, 'label', e.target.value)} />
                    <Input type="number" placeholder="Prix DA" value={v.price} onChange={(e) => updateVariant(idx, 'price', e.target.value)} />
                    <Input type="number" placeholder="Qté min" value={v.min_qty} onChange={(e) => updateVariant(idx, 'min_qty', e.target.value)} />
                    <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={v.in_stock} onChange={(e) => updateVariant(idx, 'in_stock', e.target.checked)} className="accent-[#1F4D3F]" /> Dispo</label>
                    {variants.length > 1 && (
                      <button type="button" onClick={() => setVariants(variants.filter((_, i) => i !== idx))} className="text-[#C75B27]"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button type="button" onClick={submit} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">Ajouter mon offre</Button>
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BulkToolsDialog = ({ open, onOpenChange, categories, onDone }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [percent, setPercent] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const fileRef = useRef(null);

  const renewAll = async () => {
    if (!confirm("Confirmer la disponibilité de TOUS vos produits (refresh date de mise à jour) ?")) return;
    setBusy(true);
    try {
      const res = await SupplierAPI.renewAll(null);
      toast({ title: `${res.renewed_variants} variantes actualisées`, description: `Sur ${res.products_count} produits` });
      onDone();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec' });
    } finally { setBusy(false); }
  };

  const applyPercent = async () => {
    const p = Number(percent);
    if (Number.isNaN(p) || p === 0) { toast({ title: 'Pourcentage invalide' }); return; }
    if (!confirm(`Appliquer ${p > 0 ? '+' : ''}${p}% à ${bulkCategory ? 'la catégorie sélectionnée' : 'tous vos produits'} ?`)) return;
    setBusy(true);
    try {
      const res = await SupplierAPI.bulkPriceUpdate(p, bulkCategory || null);
      toast({ title: `${res.updated} prix mis à jour`, description: `Facteur ${res.factor.toFixed(3)}` });
      setPercent('');
      onDone();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec' });
    } finally { setBusy(false); }
  };

  const handleCSV = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const res = await SupplierAPI.importCSV(f);
      toast({ title: 'Import terminé', description: `${res.created} créés · ${res.updated} mis à jour · ${res.rows_parsed} lignes` });
      onDone();
    } catch (err) {
      toast({ title: 'Erreur import', description: err.response?.data?.detail || 'Format invalide — utilisez Excel (.xlsx) ou CSV' });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const sampleCSV = `name,brand,category,image,variant_label,price,stock
Composite Tetric EvoCeram,Ivoclar Vivadent,obturation,,Seringue 4g - A2,8500,50
Composite Tetric EvoCeram,Ivoclar Vivadent,obturation,,Seringue 4g - A3,8700,50
Composite Tetric EvoCeram,Ivoclar Vivadent,obturation,,Kit 5 seringues,41000,20
Septanest Articaine 4%,Septodont,anesthesiques,,Boite 50 carpules 1/100 000,3800,100
Septanest Articaine 4%,Septodont,anesthesiques,,Boite 100 carpules 1/100 000,7400,60
Gants nitrile non poudres,Unigloves,hygiene,,Boite 100 Taille S,780,200
Gants nitrile non poudres,Unigloves,hygiene,,Boite 100 Taille M,780,300
Gants nitrile non poudres,Unigloves,hygiene,,Boite 100 Taille L,780,200
ProTaper Gold,Dentsply Sirona,endodontie,,Coffret complet 25mm,18500,30
ProTaper Gold,Dentsply Sirona,endodontie,,Recharge F1 6 limes,6200,80
NSK Pana-Air Turbine,NSK,instruments,,Standard sans fibre optique,28500,10
Masques 3 plis Type IIR,Unigloves,hygiene,,Boite 50 Bleu,365,500
AH Plus ciment canalaire,Dentsply Sirona,endodontie,,Seringue duo 4g,4800,80
Fuji IX GP Extra,GC,obturation,,Kit A2,6800,60
Sachets sterilisation autoclave,Hygitech,hygiene,,90x135mm Boite 200,1350,150`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1F4D3F]">Outils en masse</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Renouveler toutes les disponibilités */}
          <section className="border border-[#E8E0D0] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                <RefreshCw className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[#2D2A26]">Confirmer toutes les disponibilités</h3>
                <p className="text-sm text-[#7A6F5C] mt-1">Met à jour la date de fraîcheur de tous vos produits en un clic (sans modifier prix ni stock).</p>
                <Button onClick={renewAll} disabled={busy} className="mt-3 bg-emerald-700 hover:bg-emerald-800 text-white">
                  <RefreshCw className="w-4 h-4 mr-1" /> Tout renouveler
                </Button>
              </div>
            </div>
          </section>

          {/* Mise à jour en masse des prix */}
          <section className="border border-[#E8E0D0] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <Percent className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[#2D2A26]">Mise à jour des prix (+/- %)</h3>
                <p className="text-sm text-[#7A6F5C] mt-1">Augmentez ou réduisez tous vos prix d'un pourcentage fixe.</p>
                <div className="mt-3 grid grid-cols-[100px_1fr_auto] gap-2">
                  <Input type="number" placeholder="% (ex: -10)" value={percent} onChange={(e) => setPercent(e.target.value)} />
                  <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} className="border border-[#E8E0D0] rounded-md px-2 text-sm">
                    <option value="">Toutes les catégories</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <Button onClick={applyPercent} disabled={busy || !percent} className="bg-amber-700 hover:bg-amber-800 text-white">
                    Appliquer
                  </Button>
                </div>
                <p className="text-[11px] text-[#7A6F5C] mt-2">Astuce : utilisez −10 pour baisser de 10%, +15 pour augmenter de 15%.</p>
              </div>
            </div>
          </section>

          {/* Import CSV */}
          <section className="border border-[#E8E0D0] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Upload className="w-5 h-5 text-blue-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[#2D2A26]">Importer un catalogue Excel / CSV</h3>
                <p className="text-sm text-[#7A6F5C] mt-1">
                  Importez vos produits en masse. Colonnes requises :
                  <code className="text-xs bg-[#F5F1EA] px-1 mx-1 rounded">name, brand, category, variant_label, price</code>
                  · optionnelles : <code className="text-xs bg-[#F5F1EA] px-1 mx-1 rounded">image, stock</code>
                </p>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleCSV} className="hidden" id="csv-input" />
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-blue-700 hover:bg-blue-800 text-white">
                    <Upload className="w-4 h-4 mr-1" /> Choisir un fichier Excel ou CSV
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const xlsxLink = document.createElement('a');
                      xlsxLink.href = '/uploads/comptoir-online-template.xlsx';
                      xlsxLink.download = 'comptoir-online-template.xlsx';
                      xlsxLink.click();
                      return;
                      const blob = new Blob([sampleCSV], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = '/uploads/comptoir-online-template.xlsx'; a.download = 'comptoir-online-template.xlsx'; a.click(); return;
                      URL.revokeObjectURL(url);
                    }}
                    className="border-[#E8E0D0] text-[#5A5247]"
                  >
                    Télécharger un exemple
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Supplier Settings (profile + shipping policy) ============
const SupplierSettingsPanel = () => {
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    name: '', city: '', description: '',
    shipping_fee: 0, free_shipping_threshold: 0,
    express_same_day: false, express_cities: [], delivery_note: '',
  });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [cityFilter, setCityFilter] = useState('');

  useEffect(() => {
    SupplierAPI.me().then((p) => {
      setProfile(p);
      setForm({
        name: p.name || '',
        city: p.city || '',
        description: p.description || '',
        shipping_fee: p.shipping_fee ?? 0,
        free_shipping_threshold: p.free_shipping_threshold ?? 0,
        express_same_day: !!p.express_same_day,
        express_cities: Array.isArray(p.express_cities) ? p.express_cities : (p.express_same_day ? ['Alger'] : []),
        delivery_note: p.delivery_note || '',
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const toggleExpressCity = (w) => {
    setForm((f) => ({
      ...f,
      express_cities: f.express_cities.includes(w)
        ? f.express_cities.filter((c) => c !== w)
        : [...f.express_cities, w],
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const updated = await SupplierAPI.updateMe({
        name: form.name,
        city: form.city,
        description: form.description || null,
        shipping_fee: Number(form.shipping_fee) || 0,
        free_shipping_threshold: Number(form.free_shipping_threshold) || 0,
        express_same_day: !!form.express_same_day,
        express_cities: form.express_same_day ? form.express_cities : [],
        delivery_note: form.delivery_note || null,
      });
      setProfile(updated);
      toast({ title: '✅ Paramètres enregistrés' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setBusy(false); }
  };

  if (!loaded) return <div className="text-center text-[#7A6F5C] py-10">Chargement...</div>;
  if (!profile) return <div className="text-center text-[#7A6F5C] py-10">Profil fournisseur introuvable.</div>;

  return (
    <div className="space-y-6">
      {/* Profil */}
      <section className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
        <h2 className="font-serif text-lg font-semibold text-[#1F4D3F] mb-1">Profil de la société</h2>
        <p className="text-sm text-[#7A6F5C] mb-4">Ces informations sont visibles par les dentistes lors de l'achat.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Nom de la société</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-settings-name" />
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Ville / Wilaya</label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ex: 25 - Constantine" data-testid="supplier-settings-city" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Description courte (optionnel)</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Spécialiste de l'endodontie depuis 2010..." />
          </div>
        </div>
      </section>

      {/* Politique de livraison */}
      <section className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Truck className="w-5 h-5 text-[#1F4D3F]" />
          <h2 className="font-serif text-lg font-semibold text-[#1F4D3F]">Politique de livraison</h2>
        </div>
        <p className="text-sm text-[#7A6F5C] mb-4">
          Vous fixez vos frais de port et le seuil au-delà duquel la livraison devient gratuite. Les dentistes voient un compteur en temps réel sur la page produit et dans leur panier — c'est un excellent levier pour augmenter la valeur des commandes.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Frais de livraison standard (DA)</label>
            <Input
              type="number" min="0"
              value={form.shipping_fee}
              onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })}
              placeholder="Ex: 800"
              data-testid="supplier-settings-shipping-fee"
            />
            <div className="text-xs text-[#7A6F5C] mt-1">Mettez 0 pour offrir la livraison sans condition.</div>
          </div>
          <div>
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Seuil livraison gratuite (DA)</label>
            <Input
              type="number" min="0"
              value={form.free_shipping_threshold}
              onChange={(e) => setForm({ ...form, free_shipping_threshold: e.target.value })}
              placeholder="Ex: 15000"
              data-testid="supplier-settings-threshold"
            />
            <div className="text-xs text-[#7A6F5C] mt-1">Au-delà de ce montant, la livraison est offerte au client. Mettez 0 pour désactiver.</div>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="supplier-express"
              checked={form.express_same_day}
              onChange={(e) => {
                const checked = e.target.checked;
                let nextCities = form.express_cities;
                if (checked && form.express_cities.length === 0 && form.city) {
                  // Match supplier base city against canonical WILAYA labels (e.g. 'Alger' → '16 - Alger')
                  const match = WILAYAS.find(w => w.toLowerCase().endsWith(' ' + form.city.toLowerCase()) || w.toLowerCase() === form.city.toLowerCase());
                  if (match) nextCities = [match];
                }
                setForm({ ...form, express_same_day: checked, express_cities: nextCities });
              }}
              className="w-4 h-4 rounded border-[#E8E0D0] text-[#1F4D3F] focus:ring-[#1F4D3F]"
              data-testid="supplier-settings-express"
            />
            <label htmlFor="supplier-express" className="text-sm text-[#2D2A26] inline-flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-amber-600" /> Je propose la livraison <strong>express same-day</strong>
            </label>
          </div>

          {form.express_same_day && (
            <div className="sm:col-span-2" data-testid="express-cities-picker">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-[#2D2A26]">Wilayas couvertes par l'express same-day</label>
                <button
                  type="button"
                  onClick={() => setCityPickerOpen(!cityPickerOpen)}
                  data-testid="express-cities-toggle"
                  className="text-xs text-[#1F4D3F] hover:underline">
                  {cityPickerOpen ? 'Fermer' : (form.express_cities.length > 0 ? 'Modifier' : 'Choisir les wilayas →')}
                </button>
              </div>

              {form.express_cities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {form.express_cities.map((w) => (
                    <span key={w} data-testid={`express-city-tag-${w}`}
                      className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 text-xs px-2 py-1 rounded-full">
                      <Zap className="w-3 h-3" /> {w}
                      <button type="button" onClick={() => toggleExpressCity(w)} className="hover:text-amber-700">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {cityPickerOpen && (
                <div className="border border-[#E8E0D0] rounded-lg p-3 bg-[#FAF7F2] max-h-64 overflow-y-auto">
                  <Input
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    placeholder="Filtrer (ex: Alg, Oran...)"
                    data-testid="express-cities-filter"
                    className="mb-2 h-8 text-sm bg-white"
                  />
                  <div className="grid grid-cols-2 gap-1">
                    {WILAYAS.filter(w => w.toLowerCase().includes(cityFilter.toLowerCase())).map((w) => {
                      const checked = form.express_cities.includes(w);
                      return (
                        <label key={w} data-testid={`express-city-option-${w}`}
                          className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-xs">
                          <input type="checkbox" checked={checked} onChange={() => toggleExpressCity(w)}
                            className="w-3.5 h-3.5 accent-[#1F4D3F]" />
                          <span className={checked ? 'text-[#1F4D3F] font-medium' : 'text-[#5A5247]'}>{w}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-xs text-[#7A6F5C] mt-1">
                Les dentistes de ces wilayas verront un badge "Livraison express same-day" sur vos produits.
              </p>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="text-sm text-[#2D2A26] mb-1.5 block">Note de livraison (optionnel)</label>
            <Input
              value={form.delivery_note}
              onChange={(e) => setForm({ ...form, delivery_note: e.target.value })}
              placeholder="Ex: Livraison sous 24-48h ouvrées · Expédition mardi/jeudi"
              data-testid="supplier-settings-note"
            />
          </div>
        </div>

        {/* Aperçu en direct */}
        <div className="mt-5 bg-emerald-50/70 border border-emerald-200 rounded-lg p-3">
          <div className="text-xs text-emerald-800 uppercase tracking-wider mb-1">Aperçu côté client</div>
          {(() => {
            const expressTxt = form.express_same_day && form.express_cities.length > 0
              ? ` · ⚡ Express same-day à ${form.express_cities.slice(0, 3).join(', ')}${form.express_cities.length > 3 ? `, +${form.express_cities.length - 3}` : ''}`
              : (form.express_same_day ? ' · ⚡ Express same-day disponible' : '');
            if (Number(form.free_shipping_threshold) > 0) {
              return (
                <div className="text-sm text-emerald-900">
                  🚚 <strong>Livraison {Number(form.shipping_fee) > 0 ? `${form.shipping_fee} DA` : 'offerte'}</strong>
                  {' '}— gratuite dès <strong>{Number(form.free_shipping_threshold).toLocaleString('fr-DZ')} DA</strong> d'achat chez {form.name || 'votre société'}
                  {expressTxt}
                </div>
              );
            }
            if (Number(form.shipping_fee) > 0) {
              return (
                <div className="text-sm text-emerald-900">
                  🚚 <strong>Livraison {form.shipping_fee} DA</strong> sur toutes les commandes chez {form.name || 'votre société'}
                  {expressTxt}
                </div>
              );
            }
            return (
              <div className="text-sm text-emerald-900">
                🎉 <strong>Livraison gratuite sans condition</strong> chez {form.name || 'votre société'}
                {expressTxt}
              </div>
            );
          })()}
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="supplier-settings-save">
          {busy ? 'Sauvegarde...' : 'Enregistrer les paramètres'}
        </Button>
      </div>
    </div>
  );
};

const StoreManager = () => {
  const { toast } = useToast();
  const [store, setStore] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState('');
  const [myProducts, setMyProducts] = React.useState([]);
  const [cats, setCats] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const origin = (typeof window !== 'undefined') ? window.location.origin : 'https://comptoir-online.com';

  const loadProducts = React.useCallback(() => {
    SupplierAPI.products().then(setMyProducts).catch(() => {});
  }, []);

  React.useEffect(() => {
    SupplierAPI.me().then(setStore).catch(() => {});
    CatalogAPI.categories().then(setCats).catch(() => {});
    loadProducts();
  }, [loadProducts]);

  React.useEffect(() => {
    if (search.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      CatalogAPI.products({ q: search, limit: 8, include_empty: true }).then(setSearchResults).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  if (!store) return <div className="py-12 text-center text-[#7A6F5C]">Chargement…</div>;

  const sid = store.id;
  const storeUrl = `${origin}/store/${store.store_slug || ''}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(storeUrl)}`;
  const setField = (k, v) => setStore({ ...store, [k]: v });
  const catName = (id) => (cats.find(c => c.id === id) || {}).name || id;

  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const res = await SupplierAPI.uploadImage(file);
      const url = res.url || (res.file_id ? `/api/files/${res.file_id}` : '');
      setStore((s) => ({ ...s, [field]: url }));
      toast({ title: 'Image téléchargée' });
    } catch (e) { toast({ title: 'Erreur', description: "Échec de l'upload" }); }
    setUploading('');
  };

  const saveStore = async () => {
    setSaving(true);
    try {
      await SupplierAPI.updateMe({
        store_name: store.store_name || store.name,
        store_logo: store.store_logo || '',
        store_banner: store.store_banner || '',
        store_description: store.store_description || '',
        store_active: store.store_active !== false,
      });
      toast({ title: 'Boutique enregistrée ✅' });
    } catch (e) { toast({ title: 'Erreur', description: "Échec de l'enregistrement" }); }
    setSaving(false);
  };

  const copyLink = () => { navigator.clipboard?.writeText(storeUrl); toast({ title: 'Lien copié !' }); };

  const myVariant = (p) => (p.variants || []).find(v => v.supplier_id === sid) || {};

  const updateOffer = async (p, patch) => {
    const v = myVariant(p);
    try {
      await SupplierAPI.updateAvailability({ product_id: p.id, variant_label: v.label, ...patch });
      loadProducts();
    } catch (e) { toast({ title: 'Erreur', description: 'Mise à jour échouée' }); }
  };

  const changeImage = async (p, file) => {
    if (!file) return;
    setUploading(p.id);
    try {
      const res = await SupplierAPI.uploadImage(file);
      const url = res.url || (res.file_id ? `/api/files/${res.file_id}` : '');
      await SupplierAPI.setProductImage(p.id, url);
      toast({ title: 'Photo mise à jour' });
      loadProducts();
    } catch (e) { toast({ title: 'Erreur', description: 'Échec photo' }); }
    setUploading('');
  };

  const changeCategory = async (p, category) => {
    try { await SupplierAPI.setProductCategory(p.id, category); loadProducts(); }
    catch (e) { toast({ title: 'Erreur' }); }
  };

  const addExisting = async (prod) => {
    try {
      await SupplierAPI.addOffer({ product_id: prod.id, variants: [{ label: 'Unité', price: 0, min_qty: 1, stock: 999, in_stock: true, supplier_id: sid }] });
      toast({ title: 'Produit ajouté à votre boutique' });
      setSearch(''); setSearchResults([]); loadProducts();
    } catch (e) { toast({ title: 'Erreur', description: "Échec de l'ajout" }); }
  };

  const sorted = [...myProducts].sort((a, b) => {
    const oa = myVariant(a).store_order ?? 9999, ob = myVariant(b).store_order ?? 9999;
    if (oa !== ob) return oa - ob;
    return (a.name || '').localeCompare(b.name || '');
  });

  const move = async (idx, dir) => {
    const arr = [...sorted];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    const a = arr[idx], b = arr[j];
    await updateOffer(a, { store_order: j });
    await updateOffer(b, { store_order: idx });
  };

  const leafCats = cats.filter(c => c.level === 3 || (c.level === 2 && !cats.some(x => x.parent_id === c.id)));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
          <h3 className="font-serif text-lg text-[#2D2A26] mb-4">Personnaliser ma boutique</h3>
          <div className="mb-4">
            <label className="text-xs font-medium text-[#5A5247] block mb-1">Bannière</label>
            <div className="relative h-28 rounded-xl overflow-hidden bg-gradient-to-r from-[#1F4D3F] to-[#2D6452] flex items-center justify-center">
              {store.store_banner ? <img src={resolveImage(store.store_banner)} alt="" className="w-full h-full object-cover" /> : <span className="text-white/70 text-sm">Aucune bannière</span>}
              <label className="absolute bottom-2 right-2 bg-white/90 text-[#1F4D3F] text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:bg-white">
                {uploading === 'store_banner' ? '...' : 'Changer'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload('store_banner', e.target.files[0])} />
              </label>
            </div>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div className="relative w-16 h-16 rounded-2xl overflow-hidden bg-[#FAF7F2] border border-[#E8E0D0] flex items-center justify-center shrink-0">
              {store.store_logo ? <img src={resolveImage(store.store_logo)} alt="" className="w-full h-full object-cover" /> : <Store className="w-7 h-7 text-[#D8CFC0]" />}
              <label className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center cursor-pointer text-white text-[10px]">
                {uploading === 'store_logo' ? '...' : 'Logo'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload('store_logo', e.target.files[0])} />
              </label>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-[#5A5247] block mb-1">Nom de la boutique</label>
              <Input value={store.store_name || ''} onChange={(e) => setField('store_name', e.target.value)} placeholder="Ma boutique dentaire" />
            </div>
          </div>
          <div className="mb-4">
            <label className="text-xs font-medium text-[#5A5247] block mb-1">Description</label>
            <textarea value={store.store_description || ''} onChange={(e) => setField('store_description', e.target.value)} rows={2} placeholder="Présentez votre boutique…" className="w-full px-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#2D2A26] mb-4">
            <input type="checkbox" checked={store.store_active !== false} onChange={(e) => setField('store_active', e.target.checked)} className="w-4 h-4 accent-[#1F4D3F]" />
            Boutique visible publiquement
          </label>
          <Button onClick={saveStore} disabled={saving} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 text-center h-fit">
          <h4 className="font-medium text-[#2D2A26] mb-1">Partagez votre boutique</h4>
          <img src={qrUrl} alt="QR" className="w-40 h-40 mx-auto rounded-lg border border-[#E8E0D0] mt-2" />
          <div className="mt-3 text-xs text-[#5A5247] break-all bg-[#FAF7F2] rounded-lg p-2">{storeUrl}</div>
          <div className="flex gap-2 mt-3">
            <Button onClick={copyLink} variant="outline" className="flex-1 border-[#1F4D3F] text-[#1F4D3F] hover:bg-[#FAF7F2] text-sm">Copier</Button>
            <a href={storeUrl} target="_blank" rel="noreferrer" className="flex-1 bg-[#1F4D3F] hover:bg-[#173B30] text-white text-sm rounded-md flex items-center justify-center">Voir</a>
          </div>
          <a href={qrUrl} download="qr-boutique.png" className="block mt-2 text-xs text-[#C75B27] hover:underline">Télécharger le QR</a>
        </div>
      </div>

      <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
        <h3 className="font-serif text-lg text-[#2D2A26] mb-3">Ajouter un produit à ma boutique</h3>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8F82]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un produit du catalogue…" className="pl-9" />
        </div>
        {searchResults.length > 0 && (
          <div className="mt-2 border border-[#E8E0D0] rounded-lg divide-y divide-[#F5F1EA] max-h-72 overflow-y-auto">
            {searchResults.map((r) => {
              const already = (r.variants || []).some(v => v.supplier_id === sid);
              return (
                <div key={r.id} className="flex items-center gap-3 p-2">
                  <img src={resolveImage(r.image)} alt="" className="w-10 h-10 rounded object-cover bg-[#FAF7F2]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2D2A26] truncate">{r.name}</div>
                    <div className="text-xs text-[#9A8F82]">{r.brand}</div>
                  </div>
                  {already ? <span className="text-xs text-emerald-700">Déjà dans ma boutique</span>
                    : <Button size="sm" onClick={() => addExisting(r)} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white text-xs">Ajouter</Button>}
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-[#7A6F5C] mt-2">Le produit n'existe pas ? Utilisez l'onglet « Mes produits » → « Nouveau produit ».</p>
      </div>

      <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
        <h3 className="font-serif text-lg text-[#2D2A26] mb-1">Mes produits ({sorted.length})</h3>
        <p className="text-xs text-[#7A6F5C] mb-4">Gérez photo, prix, quantité min, catégorie, ordre et visibilité.</p>
        <div className="space-y-2">
          {sorted.map((p, idx) => {
            const v = myVariant(p);
            const visible = v.visible_in_store !== false;
            return (
              <div key={p.id} className="flex flex-col md:flex-row md:items-center gap-3 bg-[#FAF7F2] rounded-xl p-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-[#1F4D3F] disabled:text-[#D8CFC0]"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === sorted.length - 1} className="p-1 text-[#1F4D3F] disabled:text-[#D8CFC0]"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-white border border-[#E8E0D0] flex items-center justify-center shrink-0">
                  {p.image ? <img src={resolveImage(p.image)} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-[#D8CFC0]" />}
                  <label className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center cursor-pointer text-white text-[9px]">
                    {uploading === p.id ? '...' : 'Photo'}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => changeImage(p, e.target.files[0])} />
                  </label>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#2D2A26] truncate">{p.name}</div>
                  <select value={p.category} onChange={(e) => changeCategory(p, e.target.value)} className="mt-1 text-xs border border-[#E8E0D0] rounded px-2 py-1 bg-white max-w-[200px]">
                    <option value={p.category}>{catName(p.category)}</option>
                    {leafCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <label className="text-[10px] text-[#7A6F5C] uppercase block">Prix DA</label>
                    <Input type="number" defaultValue={v.price} onBlur={(e) => Number(e.target.value) !== v.price && updateOffer(p, { price: Number(e.target.value) })} className="h-8 w-24 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#7A6F5C] uppercase block">Qté min</label>
                    <Input type="number" min="1" defaultValue={v.min_qty || 1} onBlur={(e) => Number(e.target.value) !== (v.min_qty || 1) && updateOffer(p, { min_qty: Number(e.target.value) })} className="h-8 w-16 text-sm" />
                  </div>
                </div>
                <button onClick={() => updateOffer(p, { visible_in_store: !visible })}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium ${visible ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-500'}`}>
                  {visible ? 'Affiché' : 'Masqué'}
                </button>
              </div>
            );
          })}
          {sorted.length === 0 && <div className="text-center text-[#7A6F5C] py-8">Aucun produit. Ajoutez-en ci-dessus.</div>}
        </div>
      </div>
    </div>
  );
};

export default SupplierDashboard;
